import type { IEntityModel } from './base'
import type { IQuestionnaire } from './questionnaire'
import type { IQuestionnaireDisplayStrategy } from './questionnaire_display_strategy'
import type { IQuestionnaireSection } from './questionnaire_section'

export enum QuestionnaireQuestionType {
  Text = 'text',
  Textarea = 'textarea',
  Number = 'number',
  Date = 'date',
  Select = 'select',
  MultiSelect = 'multi_select',
  Checkbox = 'checkbox',
  Radio = 'radio',
  Time = 'time',
  DateTime = 'date_time',
}

export interface SelectInputConfig {
  options: {
    name: string
    value: string
  }[]
  allowOther?: boolean
  customOtherName?: string
  showSlider?: boolean
}

export interface NumberInputConfig {
  integer?: boolean
  showSlider?: boolean
  max?: number
  min?: number
  maxLabel?: string
  minLabel?: string
}

type QuestionnaireInputConfigType<T extends QuestionnaireQuestionType> =
  T extends
    | QuestionnaireQuestionType.Select
    | QuestionnaireQuestionType.MultiSelect
    | QuestionnaireQuestionType.Radio
    ? SelectInputConfig
    : T extends QuestionnaireQuestionType.Number
      ? NumberInputConfig
      : undefined

export interface IQuestionnaireQuestion<
  T extends QuestionnaireQuestionType = QuestionnaireQuestionType,
  TInput = QuestionnaireInputConfigType<T>,
> extends IEntityModel {
  questionnaireId: string
  questionnaireSectionId: string
  questionnaire?: IQuestionnaire
  questionnaireSection?: IQuestionnaireSection
  description: string
  required: boolean
  label: string
  type: T
  inputConfig?: TInput | null
  index: number
  mediaUrl?: string | null
  displayStrategyId?: string
  displayStrategy?: IQuestionnaireDisplayStrategy
}

export function isSelectionInputConfig(
  inputConfig: unknown,
): inputConfig is SelectInputConfig {
  if (!inputConfig) {
    return false
  }
  return typeof inputConfig === 'object' && 'options' in inputConfig
}

export function isNumberInputConfig(
  inputConfig: unknown,
): inputConfig is NumberInputConfig {
  if (!inputConfig) {
    return false
  }
  let isInputConfigCorrect = false
  if (typeof inputConfig === 'object') {
    // check if inputConfig is empty
    if (Object.keys(inputConfig).length === 0) {
      isInputConfigCorrect = true
    } else {
      if (
        'integer' in inputConfig ||
        'showSlider' in inputConfig ||
        'max' in inputConfig ||
        'min' in inputConfig ||
        'maxLabel' in inputConfig ||
        'minLabel' in inputConfig
      ) {
        isInputConfigCorrect = true
      }
    }
  }
  return isInputConfigCorrect
}
